/**
 * courier_terms.ts — THE COURIER CONTRACT'S TERMS, named one by one (space campaign J, 2026-09-30; report §17.2). Pure:
 * no DOM, no connection — `verify:market-compare` reads every line.
 *
 * Terms only. Every line states the CURRENT rule of space_market.ts (create / accept / deliver / cancel / failCourier /
 * destroyShipCore); nothing about a settled contract changes. The ordinary courier default is the documented one — the
 * courier KEEPS surviving goods (failCourier clears the tag) — and the screen says so before anyone accepts. Collateral
 * is compared with a conservative CERTIFIED basis (the catalog's ceiling buy price — never an order-book print, which a
 * thin market or a self-trade could set).
 */
import { certifiedBasis } from '@shared/market_compare.js';
import { INSURANCE } from '@shared/space_catalog.js';

export interface CourierTermsIn {
  contractId: bigint;
  originName: string; destName: string;
  cargo: Array<{ familyKey: string; qty: number }>;
  rewardSov: bigint; collateralSov: bigint;
  state: string;                 // offered | accepted | delivered | failed | cancelled
  expiresAtMs: number; nowMs: number;
  role: 'issuer' | 'courier' | 'other';
  issuerName: string; courierName: string;
  /** The viewer's active hull: its free hold and the standing policy's payout (null = uninsured / stock). */
  activeHull: { name: string; free: number; insuredPayout: bigint | null; insurable: boolean } | null;
}
export interface TermsLine { label: string; text: string; warn?: boolean }

const fam = (k: string): string => k.replace(/_/g, ' ');
const sov = (v: bigint): string => `${v.toLocaleString('en-US')} ⓒ`;
function inMins(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m <= 0) return 'now';
  if (m < 90) return `in ${m} min`;
  return `in ${Math.floor(m / 60)} h ${m % 60} min`;
}
function clock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function courierUnits(cargo: readonly { qty: number }[]): number { return cargo.reduce((n, c) => n + Math.max(0, c.qty), 0); }

/** Collateral against the certified basis: the warning shown on the terms AND on the post form. */
export function collateralCheck(cargo: readonly { familyKey: string; qty: number }[], collateralSov: bigint): { basis: bigint; over: boolean; unknown: string[]; text: string } {
  const { basis, unknown } = certifiedBasis(cargo);
  const over = collateralSov > basis && basis > 0n;
  const text = collateralSov <= 0n
    ? `no collateral — the courier risks only the reward (certified basis of the cargo: ${sov(basis)})`
    : over
      ? `${sov(collateralSov)} is ABOVE the certified replacement basis of ${sov(basis)} (the catalog's ceiling buy price for this cargo — never a market print): a failure pays the issuer more than the goods are worth`
      : `${sov(collateralSov)} — within the certified replacement basis of ${sov(basis)} (the catalog's ceiling buy price for this cargo)`;
  return { basis, over, unknown, text };
}

export function courierTerms(i: CourierTermsIn): TermsLine[] {
  const units = courierUnits(i.cargo);
  const lines: TermsLine[] = [];
  const left = i.expiresAtMs - i.nowMs;
  lines.push({ label: 'Route', text: `${i.originName} → ${i.destName} (contract #${i.contractId}, ${i.state})` });
  const fit = i.activeHull
    ? (i.activeHull.free >= units ? ` — fits your active hull ${i.activeHull.name} (${i.activeHull.free} u free)` : ` — does NOT fit your active hull ${i.activeHull.name} (${i.activeHull.free} u free)`)
    : '';
  lines.push({ label: 'Payload', text: `${i.cargo.map((c) => `${c.qty} × ${fam(c.familyKey)}`).join(', ')} — ${units} u, sealed as one crate${fit}`, warn: !!i.activeHull && i.activeHull.free < units && i.state === 'offered' && i.role === 'other' });
  lines.push({
    label: 'Owner now',
    text: i.state === 'accepted'
      ? `${i.issuerName}'s goods, carried by ${i.courierName} (the courier is the custodian — the crate is tagged to this contract and cannot be sold, unloaded or landed)`
      : `${i.issuerName}'s goods, sealed in a manifest at ${i.originName}`,
  });
  lines.push({
    label: 'Owner after default',
    text: `If the deadline passes, or the carrying hull is destroyed, the contract FAILS: ${i.issuerName} is repaid the reward and the collateral, and the courier KEEPS whatever goods survive (the tag comes off — the ordinary courier default). Goods aboard a destroyed hull lie in its wreck for ${INSURANCE.wreckTtlS / 3600} h and anyone in range may strip them.`,
  });
  lines.push({ label: 'Reward held', text: `${sov(i.rewardSov)} — escrowed from ${i.issuerName} when the contract was posted; paid to the courier on delivery, back to the issuer on failure, cancel or an untaken lapse.` });
  const cc = collateralCheck(i.cargo, i.collateralSov);
  lines.push({
    label: 'Collateral held',
    text: `${i.collateralSov > 0n ? `${sov(i.collateralSov)} — escrowed from the courier at acceptance; returned with the reward on delivery, paid to the issuer on failure. ` : ''}${cc.text}`,
    warn: cc.over,
  });
  lines.push({ label: 'Hand-in rights', text: `Only the accepting courier, DOCKED at ${i.destName}, before the deadline, with the WHOLE crate aboard their ACTIVE hull; it lands in ${i.issuerName}'s warehouse there. A partial crate is refused; nobody else may deliver it.` });
  lines.push({
    label: 'Accept by / deliver by',
    text: `ONE clock: ${clock(i.expiresAtMs)} (${inMins(left)}). Accepting does not extend it — a courier who accepts late has only what remains to deliver.`,
    warn: i.state === 'offered' && left > 0 && left < 30 * 60_000,
  });
  lines.push({ label: 'If the endpoint cannot', text: `Registry stations never close today. If ${i.destName} ever left the registry, delivery would be impossible and the contract would fail at its deadline (no reroute) — the failure above, nothing new.` });
  const hull = i.activeHull;
  const ins = !hull ? 'no active hull' : !hull.insurable ? `your ${hull.name} is the stock hull — uninsurable, re-issued free` : hull.insuredPayout !== null ? `your ${hull.name} is insured — a loss pays ${sov(hull.insuredPayout)} for the hull alone` : `your ${hull.name} is uninsured`;
  lines.push({ label: 'Carrier loss', text: `Your hull destroyed with the crate aboard: the contract fails and the collateral goes to the issuer. Hull insurance pays only the hull — ${ins} — never the collateral, never the crate.` });
  lines.push({ label: 'Cancel', text: `${i.issuerName} may cancel only while nobody has accepted — the manifest returns to their warehouse at ${i.originName} and the reward is refunded.` });
  lines.push({ label: 'Terms only', text: 'These are the contract\'s existing rules, stated before you commit — nothing about a settled contract changes.' });
  return lines;
}
